import { useCallback, useEffect, useState } from 'react'

import { useBlocker } from 'react-router'

type PendingAction = () => void

export type UnsavedGuard = {
  /** 未保存変更の確認が必要な遷移または画面内操作が保留されているか。 */
  isBlocked: boolean
  /** 保留中の遷移または画面内操作を続行する。 */
  proceed: () => void
  /** 保留中の遷移または画面内操作を取りやめる。 */
  cancel: () => void
  /**
   * 画面内の操作・モーダルを閉じる操作を未保存ガード経由で実行する。
   * clean なら直ちに、dirty なら確認後に実行する。
   */
  guardAction: (action: PendingAction) => void
}

type UseUnsavedActionGuardOptions = {
  /** 未保存変更があるか。 */
  isDirty: boolean
  /** タブ閉じ・リロードもブラウザ標準の確認で防ぐか。既定値は true。 */
  beforeUnload?: boolean
  /**
   * 画面内 action を保留するかを route 離脱とは独立して判定する slot。
   * 未指定時は isDirty を使う。
   */
  shouldBlockAction?: () => boolean
}

type UseUnsavedGuardOptions = UseUnsavedActionGuardOptions & {
  /**
   * ルート遷移がブロックされたときの確認 UI adapter。
   * 指定しない場合は返却値を使って呼び出し側が dialog を描画する。
   */
  onRouteBlocked?: (actions: Pick<UnsavedGuard, 'proceed' | 'cancel'>) => void
}

/**
 * 未保存変更がある場合のタブ閉じ・任意の画面内操作を一元的に防ぐ。
 *
 * route protocol を必要としないモーダルはこの hook を使うため、React Router の
 * blocker へ登録されない。route をまたぐ画面は `useUnsavedGuard` を使う。
 */
export function useUnsavedActionGuard({
  isDirty,
  beforeUnload = true,
  shouldBlockAction,
}: UseUnsavedActionGuardOptions): UnsavedGuard {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

  const proceed = useCallback((): void => {
    if (pendingAction !== null) {
      pendingAction()
      setPendingAction(null)
    }
  }, [pendingAction])

  const cancel = useCallback((): void => {
    setPendingAction(null)
  }, [])

  useEffect(() => {
    if (!beforeUnload || !isDirty) return
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [beforeUnload, isDirty])

  const guardAction = useCallback(
    (action: PendingAction): void => {
      if (shouldBlockAction?.() ?? isDirty) {
        setPendingAction(() => action)
        return
      }
      action()
    },
    [isDirty, shouldBlockAction],
  )

  return {
    isBlocked: pendingAction !== null,
    proceed,
    cancel,
    guardAction,
  }
}

/**
 * 未保存変更がある場合の route 離脱・タブ閉じ・任意の画面内操作を一元的に防ぐ。
 *
 * React Router と beforeunload の protocol、保留 action の lifecycle を隠し、
 * 各画面は dirty 判定と確認 UI adapter だけを渡す。
 */
export function useUnsavedGuard({
  isDirty,
  beforeUnload = true,
  shouldBlockAction,
  onRouteBlocked,
}: UseUnsavedGuardOptions): UnsavedGuard {
  const {
    isBlocked: isActionBlocked,
    proceed: proceedAction,
    cancel: cancelAction,
    guardAction,
  } = useUnsavedActionGuard({
    isDirty,
    beforeUnload,
    shouldBlockAction,
  })
  const blocker = useBlocker(isDirty)

  const proceed = useCallback((): void => {
    if (blocker.state === 'blocked') {
      blocker.proceed()
      return
    }
    proceedAction()
  }, [blocker, proceedAction])

  const cancel = useCallback((): void => {
    if (blocker.state === 'blocked') blocker.reset()
    cancelAction()
  }, [blocker, cancelAction])

  useEffect(() => {
    if (blocker.state === 'blocked') onRouteBlocked?.({ proceed, cancel })
  }, [blocker.state, cancel, onRouteBlocked, proceed])

  return {
    isBlocked: blocker.state === 'blocked' || isActionBlocked,
    proceed,
    cancel,
    guardAction,
  }
}
